import type { ZipkinTraceSpan, SpanData } from './zipkin-trace-chart';
import { calculateSpanData, getTimeRange, getStatusClassName } from './utils';
import type { StackFrameType } from '@cotera/api';

// RunData type with phase timing data for visual segments
export type RunData = StackFrameType & {
  // Phase timing data for visual segments within the span
  phaseTiming?: {
    waitingStartTime: number | null;
    allocatedStartTime: number | null;
    executionStartTime: number | null;
    endTime: number;
  };
};

export const nameForProgram = (runData: RunData) => {
  // For 'program' type, show 'workflow' (displayName will be 'program' from server)
  if (runData.fpType === 'program') {
    return 'workflow';
  }
  // Use displayName from server (tool display_name for tools, fpType for others)
  return runData.displayName;
};

/**
 * Calculate start time for a frame based on its events
 */
function calculateFrameStartTime(frame: StackFrameType): number {
  if (frame.events.length > 0) {
    const eventTimes = frame.events.map((e) => new Date(e.createdAt).getTime());
    return Math.min(...eventTimes);
  }
  // Fallback to createdAt if no events
  return new Date(frame.createdAt).getTime();
}

/**
 * Calculate end time for a frame based on its status and events
 */
function calculateFrameEndTime(frame: StackFrameType): number {
  const isInProgress =
    frame.status === 'pending' ||
    frame.status === 'running' ||
    frame.status === 'allocated' ||
    frame.status === 'queued' ||
    frame.status === 'waiting' ||
    frame.status === 'stalled';

  if (frame.events.length > 0) {
    const eventTimes = frame.events.map((e) => new Date(e.createdAt).getTime());
    // For in-progress runs, use current time as end to show elapsed time
    return isInProgress ? Date.now() : Math.max(...eventTimes);
  }

  // Fallback to endedAt/current time if no events
  return frame.endedAt ? new Date(frame.endedAt).getTime() : Date.now();
}

/**
 * Converts flat array of frames to ZipkinTraceSpan format for the ZipkinTraceChart component
 * Works directly with the flat array using depth and parentId fields from the backend
 */
export const convertRunDataToTraceSpans = (
  frames: StackFrameType[]
): ZipkinTraceSpan<RunData>[] => {
  if (frames.length === 0) {
    return [];
  }

  // Calculate start/end times for all frames
  // For in-progress frames, end time is Date.now() which naturally encompasses all children
  // For completed frames, parent times should already reflect when children completed
  return frames
    .sort((a, b) => a.position - b.position)
    .map((frame) => {
      const startTime = calculateFrameStartTime(frame);
      const endTime = calculateFrameEndTime(frame);

      // Calculate phase boundaries for visual segments
      const waitingStartTime = startTime;
      const allocatedStartTime = frame.allocatedAt
        ? new Date(frame.allocatedAt).getTime()
        : null;
      const executionStartTime = frame.startedAt
        ? new Date(frame.startedAt).getTime()
        : null;

      const runData: RunData = {
        ...frame,
        phaseTiming: {
          waitingStartTime,
          allocatedStartTime,
          executionStartTime,
          endTime,
        },
      };

      return {
        id: frame.id,
        name: nameForProgram(runData),
        startTime,
        duration: endTime - startTime,
        status: frame.status,
        parentId: frame.parentId ?? undefined,
        className: getStatusClassName(frame.status),
        events: frame.events,
        data: runData,
      };
    });
};

/**
 * Converts flat array of frames to SpanData<RunData> format with run data included in the data field
 */
export const convertRunDataToSpanData = (
  frames: StackFrameType[]
): SpanData<RunData>[] => {
  const spans = convertRunDataToTraceSpans(frames);
  const timeRange = getTimeRange(spans);

  return calculateSpanData(spans, timeRange);
};
